import type { Follow } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  InfoButton,
  Label,
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import { useDebounceCallback } from "usehooks-ts"
import styles from "./Ranges.module.scss"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useSegment } from "src/contexts/SegmentContext"
import {
  useSliderInputProps,
  useGetGlucoseValue,
  useSetGlucoseValue,
  useGlucoseInputProps,
  useReadableGlucoseUnit,
} from "src/hooks"

const polyglotPrefix = "pages.settings.ranges.urgent_low."

const inputProps = {
  min: 40,
  max: 100,
}
const numberId = "urgent-low-number"

interface Props {
  updatedAt?: string;
  updateFollow: (settings: Partial<Follow>) => void;
}

export function UrgentLow(props: Props) {
  const {
    updatedAt,
    updateFollow,
  } = props

  const { currentFollow } = useFollowedUsers()
  const polyglot = usePolyglot()
  const readableGlucoseUnit = useReadableGlucoseUnit()

  const getGlucoseValue = useGetGlucoseValue()
  const setGlucoseValue = useSetGlucoseValue()
  const displaySettings = currentFollow.display_settings
  const currentUrgentLow = displaySettings.urgent_low

  const analyticsRef = useSegment()

  const trackUpdate = useDebounceCallback(
    useCallback(
      (threshold: number) => {
        void analyticsRef.current.track(
          "Urgent Low Threshold Set",
          {
            Threshold: threshold,
          },
        )
      },
      [analyticsRef],
    ),
    3000,
  )

  const onValueChange = useCallback(
    (urgentLow: Follow["display_settings"]["urgent_low"]) => {
      updateFollow({
        display_settings: {
          ...displaySettings,
          urgent_low: setGlucoseValue(urgentLow),
        },
      })

      trackUpdate(urgentLow)
    },
    [
      updateFollow,
      displaySettings,
      trackUpdate,
      setGlucoseValue,
    ],
  )

  const sharedProps = useGlucoseInputProps(inputProps)

  const {
    inputValue,
    sliderValue,
    onSliderChange,
    onNumberChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    value: getGlucoseValue(currentUrgentLow),
    onChange: onValueChange,
    updatedAt,
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
    toFixed: true,
  })

  return (
    <div>
      <Label>
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}title`)}>
          <div>
            {polyglot.t(`${polyglotPrefix}description_one`)}
          </div>
          <div>
            {polyglot.t(`${polyglotPrefix}description_two`)}
          </div>
        </InfoButton>
      </Label>
      <div className={styles.inputContainer}>
        <SliderValue
          sizePreset="small"
          alignmentPreset="center"
        >
          <SliderValueInput
            {...sharedProps}
            placeholder=""
            value={inputValue}
            onChange={onNumberChange}
            onBlur={onNumberBlur}
            onKeyDown={onNumberKeyDown}
            id={numberId}
          />
          <SliderValueLabelContainer>
            <SliderValueLabel htmlFor={numberId}>
              {readableGlucoseUnit}
            </SliderValueLabel>
          </SliderValueLabelContainer>
        </SliderValue>
        <Slider
          className={styles.rangeInputSlider}
          id="urgent-low-slider-container"
        >
          <SliderInput
            {...sharedProps}
            value={sliderValue}
            onChange={onSliderChange}
            id="urgent-low-slider"
          />
          <SliderTracks
            min={sharedProps.min}
            max={sharedProps.max}
          />
        </Slider>
      </div>
    </div>
  )
}
